import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, CalendarCheck, MessageCircle } from "lucide-react";
import { getIcon, track } from "@/components/landing/shared";
import { getImageUrl } from "@/utils/imageUrl";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ServicePageTemplate({ slug }) {
  const [page, setPage] = useState(null);
  const [generalContact, setGeneralContact] = useState(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/site-content`).then(r => r.data?.service_pages?.[slug]).catch(() => null),
      axios.get(`${API}/api/public/general-contact`).then(r => r.data).catch(() => null),
    ]).then(([p, gc]) => { setPage(p); setGeneralContact(gc); });
  }, [slug]);

  if (!page) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full" /></div>;

  const services = page.services || [];
  const advantages = page.advantages || [];
  const gallery = page.gallery || [];
  const waPhoneClean = generalContact?.whatsapp_phone_clean || "";
  const sv = page.sections_visibility || {};
  const show = (key) => sv[key] !== false;

  return (
    <div className="bg-white" data-testid={`service-page-${slug}`}>

      {/* ─── HERO ─── */}
      <section className="relative min-h-[50vh] flex items-end overflow-hidden" data-testid="service-hero">
        <div className="absolute inset-0">
          {page.hero_image ? (
            <img src={getImageUrl(page.hero_image)} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-slate-900" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/50 to-slate-900/30" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-32 w-full">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-10 bg-red-600" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-red-400 font-semibold">Servicio Oficial</span>
          </div>
          <h1 className="heading-industrial text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white uppercase leading-tight" data-testid="service-title">{page.title}</h1>
          <p className="text-base sm:text-lg text-slate-300 mt-4 max-w-2xl leading-relaxed">{page.subtitle}</p>
        </div>
      </section>

      {/* ─── DESCRIPTION ─── */}
      {show("description") && page.description && (
        <section className="py-20 lg:py-24 bg-white" data-testid="service-description">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed">{page.description}</p>
          </div>
        </section>
      )}

      {/* ─── GALLERY ─── */}
      {show("gallery") && gallery.length > 0 && (
        <section className="py-16 lg:py-20 bg-slate-50" data-testid="service-gallery">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`grid gap-3 ${
              gallery.length === 1 ? "grid-cols-1" :
              gallery.length === 2 ? "grid-cols-1 sm:grid-cols-2" :
              gallery.length === 3 ? "grid-cols-1 sm:grid-cols-3" :
              "grid-cols-2 lg:grid-cols-4"
            }`}>
              {gallery.sort((a, b) => (a.order || 0) - (b.order || 0)).map((img, i) => (
                <div
                  key={i}
                  className={`overflow-hidden group ${i === 0 && gallery.length > 2 ? "sm:col-span-2 sm:row-span-2" : ""}`}
                  data-testid={`gallery-img-${i}`}
                >
                  <img
                    src={getImageUrl(img.image_url)}
                    alt={img.alt || ""}
                    className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── SERVICES ─── */}
      {show("services") && services.length > 0 && (
        <section className="py-20 lg:py-24 bg-white" data-testid="service-list">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-xl mb-14">
              <span className="text-[11px] uppercase tracking-[0.3em] text-red-600 font-semibold">{page.services_title || "Nuestros servicios"}</span>
              <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 mt-3 uppercase">{page.services_subtitle || "Que podemos hacer por ti"}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {services.map((s, i) => {
                const Icon = getIcon(s.icon);
                return (
                  <div key={i} className="bg-white border border-slate-200 p-7 group hover:border-slate-300 hover:shadow-md transition-all duration-300" data-testid={`service-item-${i}`}>
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-red-600/10 flex items-center justify-center flex-shrink-0 group-hover:bg-red-600 transition-colors duration-300">
                        <Icon className="w-5 h-5 text-red-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <h3 className="heading-industrial text-sm font-bold text-slate-900 uppercase tracking-wider mb-2">{s.title}</h3>
                        <p className="text-sm text-slate-500 leading-relaxed">{s.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ─── ADVANTAGES ─── */}
      {show("advantages") && advantages.length > 0 && (
        <section className="py-20 lg:py-24 bg-slate-50" data-testid="service-advantages">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[11px] uppercase tracking-[0.3em] text-red-600 font-semibold">{page.advantages_title || "Por que elegirnos"}</span>
              <h2 className="heading-industrial text-2xl sm:text-3xl font-bold text-slate-900 mt-3 uppercase">{page.advantages_subtitle || "Ventajas del Servicio Oficial"}</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {advantages.map((a, i) => (
                <div key={i} className="text-center py-8 px-5 border border-slate-200 bg-white hover:border-red-200 hover:shadow-sm transition-all duration-300" data-testid={`service-advantage-${i}`}>
                  <div className="w-14 h-14 bg-slate-900 flex items-center justify-center mx-auto mb-4">
                    <span className="heading-industrial text-xl font-bold text-white">{String(i + 1).padStart(2, "0")}</span>
                  </div>
                  <h3 className="heading-industrial text-sm font-bold text-slate-900 uppercase tracking-wider mb-2">{a.title}</h3>
                  <p className="text-sm text-slate-500">{a.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── CTA FINAL ─── */}
      {show("cta") && (
        <section className="py-20 lg:py-24 bg-slate-900 relative" data-testid="service-cta">
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: "repeating-linear-gradient(90deg, rgba(255,255,255,.07) 0 1px, transparent 1px 60px)" }} />
          <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-16 bg-red-600 flex items-center justify-center mx-auto mb-8">
              <CalendarCheck className="w-8 h-8 text-white" />
            </div>
            <h2 className="heading-industrial text-2xl sm:text-3xl lg:text-4xl font-bold text-white uppercase mb-5">{page.cta_title || "Pide tu cita ahora"}</h2>
            <p className="text-slate-400 mb-10 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">{page.cta_text || "Contactanos y te atendemos sin compromiso."}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/cita" onClick={() => track(`service_${slug}_cta`, "reservar_cita")}>
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-none text-sm uppercase tracking-wider font-bold h-14 px-12 shadow-lg shadow-red-600/30" data-testid="service-cta-btn">
                  Reservar Cita <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              {waPhoneClean && (
                <a href={`https://wa.me/${waPhoneClean}`} target="_blank" rel="noopener noreferrer"
                  onClick={() => track(`service_${slug}_whatsapp`, "whatsapp")}
                  className="inline-flex items-center justify-center gap-2 border border-slate-600 text-slate-300 hover:bg-white/5 hover:border-slate-400 rounded-none text-sm uppercase tracking-wider font-bold h-14 px-10 transition-colors"
                  data-testid="service-whatsapp-link">
                  <MessageCircle className="w-5 h-5" /> WhatsApp
                </a>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
